/* eslint-disable max-lines */
/* eslint-disable import/max-dependencies */
import {
  useApiDelete,
  useApiFileUpload,
  useApiGet,
  useApiPatch,
  useApiPost,
  useApiQuery,
} from 'src/api';
import { useBuildVars } from 'src/config';
import { useApiHeaders } from './apiheader';
import { ColumnFilter } from 'src/components/TableComponent/types';
import { useMutation, useQueryClient } from 'react-query';
import { HeaderFormProperties } from './bidForm/headerForm';
import { ResellerFormProperties } from './bidForm/resellerForm';
import { EndUserFormProperties } from './bidForm/endUserForm';
import { BidLinesFormProperties } from './bidForm/bidLinesForm';
import { CompanyFormProperties } from './bidForm/companyForm';
import { ExtraFormPropertyMapping } from './types';
import { useEffect } from 'react';
import {
  CUSTOMER_GROUP_SEARCH_URL,
  CustomerGroup,
  CustomerGroupListResult,
} from 'src/views/customerGroups/hooks/customerGroupSearchQuery';
import { parseResponse } from 'src/views/customerGroups/hooks/hooks';
import { BidStatus } from './common';
import { useGetRegion } from 'src/util/getRegion';
import {
  AuditFilterRequest,
  AuditFilterResponse,
  AuditLogsResponse,
  AuditSearchRequest,
} from 'src/views/auditLogs/hooks/useAuditLogsHooks';

export type BidsListResponse = {
  items: {
    assignedTo: string;
    bidNumber: string;
    createdBy: string;
    createdDate: string;
    gdeRefId: number;
    hasMultipleResellers: boolean;
    masterVendor: string;
    priority: string;
    ressellerAccount: string;
    status: keyof typeof BidStatus;
    timeinQueue: number;
  }[];
  message: string;
  totalCount: number;
  transactionID: string;
  transactionStatus: string;
};

export type BidFilterRequestType = {
  filters: {
    field: string;
    operator: string;
    value: string | string[];
  }[];
};

export type BidsListRequest = {
  page: number;
  pageSize: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
} & Partial<BidFilterRequestType>;

export const filterQueryGenerator = (filters?: ColumnFilter) => {
  const filterRequest: BidFilterRequestType = { filters: [] };

  if (!filters) return filterRequest;

  for (const key of Object.keys(filters)) {
    const filter = filters[key];
    if (filter === null) continue;
    filterRequest.filters.push({
      field: key,
      operator: filter.action,
      value: filter.value,
    });
  }

  return filterRequest;
};

export type UpdateActionRequest = {
  actionType: string;
  assignedTo?: string | null;
  bidReqHdrId: number;
  comment: string;
  reason?: string;
};

export function useUpdateAction() {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  const apiPatch = useApiPatch(buildVars.VP_BMH_BASE);
  return useMutation((formData: UpdateActionRequest) =>
    apiPatch(
      `/api/v1/BidEntryManagement/BidRequestAction`,
      formData,
      undefined,
      headers
    )
  );
}

export type BidsDenyResponse = [
  {
    id: string;
    reason: string;
  }
];

export function useGetDenyReasons(enabled?: boolean) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<BidsDenyResponse>(
    `/api/v1/BidEntryManagement/GetDenyReasons`,
    undefined,
    { enabled },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export function useGetBidsList(query: BidsListRequest) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  const apiPost = useApiPost<BidsListResponse>({ host: buildVars.VP_BMH_BASE });
  const mutation = useMutation(() =>
    apiPost(
      `/api/v1/BidEntryManagement/GetBidQueueList`,
      query,
      undefined,
      headers
    )
  );
  useEffect(() => {
    mutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  return mutation;
}

export type CreateBidQueueRequest = {
  bidEndUser: EndUserFormProperties[];
  bidHeader: HeaderFormProperties;
  bidLines: BidLinesFormProperties[];
  bidReseller: ResellerFormProperties[];
  customerGroups: CompanyFormProperties[];
};

export type APIResponse = {
  message: string;
  reqHeaderId: number;
  transactionID: string;
  transactionStatus: string;
};

export function useCreateBidQueue() {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const apiPost = useApiPost<APIResponse>({ host: buildVars.VP_BMH_BASE });
  return useMutation(
    (formData: CreateBidQueueRequest) =>
      apiPost(
        `/api/v1/BidEntryManagement/CreateBidQueue`,
        formData,
        undefined,
        headers
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/api/v1/BidEntryManagement/GetBidQueueList`
        ),
    }
  );
}

export interface VendorProgramList {
  businessType: string;
  createdDate: string;
  lastModified: string;
  status: '0' | '1' | '2';
  uploadCount: string;
  vendorProgramDescription: string;
  vendorProgramId: number;
  vendorProgramName: string;
}
export interface VendorProgramLookUpResponse {
  vendorId: number;
  vendorName: string;
  vendorProgramCount: number;
  vendorProgramlist: VendorProgramList[];
}

export interface ProgramProgramLookupRequest {
  countryCode: string;
  vendorIdToSearch: number;
}

export function useVendorProgramLookup(params: ProgramProgramLookupRequest) {
  const buildVars = useBuildVars();
  return useApiQuery<VendorProgramLookUpResponse>(
    '/system.webapi.gds/api/v1/gdsCoreApiVendor/vendorProgram',
    params,
    {
      enabled:
        typeof params.vendorIdToSearch === 'number' &&
        !!params.vendorIdToSearch,
    },
    undefined,
    {
      host: buildVars.VP_API_BASE,
    }
  );
}
export type GetBidFormDataParams = {
  BidReqHdrId: number;
};

type Id<T> = T & { id: number };

export function useGetBidHeaderData(
  params: GetBidFormDataParams,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<HeaderFormProperties[]>(
    `/api/v1/BidEntryManagement/GetBidHeaderData`,
    params,
    { cacheTime: 0, enabled, queryKey: ['bidHeaderData', params] },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export function useGetBidResellerData(
  params: GetBidFormDataParams,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<Id<ResellerFormProperties>[]>(
    `/api/v1/BidEntryManagement/GetBidResellerData`,
    params,
    { cacheTime: 0, enabled, queryKey: ['bidResellerData', params] },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export function useGetBidEndUserData(
  params: GetBidFormDataParams,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<Id<EndUserFormProperties>[]>(
    `/api/v1/BidEntryManagement/GetBidEndUserData`,
    params,
    { cacheTime: 0, enabled, queryKey: ['bidEndUserData', params] },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export function useGetBidLinesData(
  params: GetBidFormDataParams,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<Id<BidLinesFormProperties>[]>(
    `/api/v1/BidEntryManagement/GetBidLinesData`,
    params,
    { cacheTime: 0, enabled, queryKey: ['bidLinesData', params] },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export function useGetBidCompanyData(
  params: GetBidFormDataParams,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<Id<CompanyFormProperties>[]>(
    `/api/v1/BidEntryManagement/GetCustomerGroupData`,
    params,
    { cacheTime: 0, enabled, queryKey: ['bidCompanyData', params] },
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}

export type UpdateBidQueueRequest = {
  bidEndUser: ExtraFormPropertyMapping<EndUserFormProperties>[];
  bidHeader: HeaderFormProperties;
  bidLines: ExtraFormPropertyMapping<BidLinesFormProperties>[];
  bidReseller: ExtraFormPropertyMapping<ResellerFormProperties>[];
  customerGroups: ExtraFormPropertyMapping<CompanyFormProperties>[];
  statusInfo?: UpdateActionRequest;
};

export function useUpdateBidQueue(reqHeaderId: number) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const apiPatch = useApiPatch<APIResponse>(buildVars.VP_BMH_BASE);
  return useMutation(
    (formData: UpdateBidQueueRequest) =>
      apiPatch(
        `/api/v1/BidEntryManagement/UpdateBidQueue/${reqHeaderId}`,
        formData,
        undefined,
        headers
      ),
    {
      onSuccess: () =>
        queryClient.invalidateQueries(
          `/api/v1/BidEntryManagement/GetBidQueueList`
        ),
    }
  );
}

export function useGenerateBidNumber() {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  const apiPost = useApiPost<{ bidNumber: string }>({
    host: buildVars.VP_BMH_BASE,
  });
  return useMutation(() =>
    apiPost(
      `/api/v1/BidEntryManagement/GenerateUniqueBidNumber`,
      {},
      undefined,
      headers
    )
  );
}

export type FileUploadResponse = {
  InvalidFiles?: string[];
  headerId: number;
  message: string;
};

export type FileUploadRequest = {
  bidNumber: string;
  companyCode: string;
  countryCode: string;
  document: File[];
  documentId?: number;
  vendorName: string;
  vendorProgramName: string;
};

export function useUploadFile(
  setProgress: (progress: number) => void,
  action: 'create' | 'update'
) {
  const buildVars = useBuildVars();
  const { upload, xhr } = useApiFileUpload<FileUploadResponse>(setProgress, {
    host: buildVars.VP_PARSING_API_BASE,
  });
  const region = useGetRegion();

  const uploadApi = useMutation((req: FileUploadRequest) => {
    const formData = new FormData();
    for (let i = 0; i < req.document.length; i++) {
      formData.append('file', req.document[i]);
    }

    formData.append(
      'metadata',
      JSON.stringify({
        bidNumber: req.bidNumber,
        bidSource: 'BEM',
        country: req.countryCode,
        folderName: 'BEM',
        isMultiUpload: true,
        vendorName: req.vendorName,
        vendorProgramName: req.vendorProgramName,
      })
    );

    const uploadUrl = `/system.webapi.parserhub/manualSftpUploadV2`;
    const url =
      action === 'create' ? `${uploadUrl}` : `${uploadUrl}/${req.documentId}`;
    return upload(`${url}?IM-Region=${encodeURIComponent(region)}`, formData, {
      'IM-Region': region,
    });
  });

  return { uploadApi, xhr };
}

export type RevisedFileUploadRequest = {
  country: string;
  existingHeaderId: number;
  revise: boolean;
  vendorName: string;
};

export const useUploadReviseFiles = () => {
  const buildVars = useBuildVars();
  const region = useGetRegion();
  const { upload } = useApiFileUpload<FileUploadResponse>(() => undefined, {
    host: buildVars.VP_PARSING_API_BASE,
  });

  return useMutation((req: RevisedFileUploadRequest) => {
    const formData = new FormData();
    formData.append(
      'metadata',
      JSON.stringify({
        country: req.country,
        existingHeaderId: req.existingHeaderId,
        folderName: 'BEM',
        revise: req.revise,
        vendorName: req.vendorName,
      })
    );
    return upload(
      `/system.webapi.parserhub/manualSftpUploadV2?IM-Region=${encodeURIComponent(
        region
      )}`,
      formData,
      {
        'IM-Region': region,
      }
    );
  });
};

export const useDeleteFile = (headerId: number) => {
  const buildVars = useBuildVars();
  const apiDelete = useApiDelete(buildVars.VP_PARSING_API_BASE);
  const region = useGetRegion();

  return useMutation((params: { fileName: string }) =>
    apiDelete(
      `/system.webapi.parserhub/manualSftpUploadV2/${headerId}`,
      undefined,
      {
        ...params,
        'IM-Region': region,
      },
      {
        'IM-Region': region,
      }
    )
  );
};

export type BidsCommentResponse = {
  comment: string;
  roleId: number;
  userEmail: string;
}[];

export function useGetBidsComment(reqHeaderId: number) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<BidsCommentResponse>(
    `/api/v1/BidEntryManagement/GetUsersComments`,
    {
      reqHeaderId,
    },
    undefined,
    headers,
    { host: buildVars.VP_BMH_BASE }
  );
}
export type GroupSearchRequest = {
  name: string;
  page: number;
  pageSize: number;
};

export function useCustomerGroupSearchQuery(params: GroupSearchRequest) {
  const buildVars = useBuildVars();

  return useApiQuery<CustomerGroupListResult>(
    CUSTOMER_GROUP_SEARCH_URL,
    params,
    {
      enabled: params.name.trim().length >= 1,
      useErrorBoundary: (error) =>
        ![400, 404].includes(error?.status as number),
    },
    undefined,
    {
      host: buildVars.CG_API_BASE,
      parseResponse: (res) => parseResponse<CustomerGroup>(res),
    }
  );
}

export type OrderDetailsResponse = {
  ingramOrderDate: any;
  orderStatus: string;
};

export const useGetOrderDetails = () => {
  const buildVars = useBuildVars();
  const apiGet = useApiGet<OrderDetailsResponse>({
    host: buildVars.VP_API_BASE,
  });

  return useMutation(
    (params: { CustomerNumber: string; OrderNumber: string }) =>
      apiGet(
        '/system.webapi.gds/api/v1/gdsCoreApiDiscountMaintenance/GetOrderStatus',
        params
      )
  );
};

export type BidNumberValidateResponse = {
  assignedTo: string;
  bidNumber: string;
  bidNumberErp: string;
  createdBy: string;
  exists: boolean;
  expiryDate: string;
  requestId: string;
  status: string;
  vendorId: number;
};

export type BidNumberValidateRequest = {
  bidNumber?: string;
  bidNumberErp?: string;
  vendorId: number;
};

export const useGetValidateBidNumber = () => {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  const apiGet = useApiGet<BidNumberValidateResponse[]>({
    host: buildVars.VP_BMH_BASE,
  });

  return useMutation((params: BidNumberValidateRequest) =>
    apiGet(`/api/v1/BidEntryManagement/validate`, params, headers)
  );
};

export type ChangeLogResponse = {
  comment: string | null;
  editReason: string | null;
  id: number;
  lastchangedby: string;
  lastchangeddate: string;
  totalcount: number;
  version: number;
};

export type ChangeLogListParams = {
  bidReqHdrId: number;
  pageNo: number;
  pageSize: number;
};

export function useGetChangeLogList(params: ChangeLogListParams) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  return useApiQuery<ChangeLogResponse[]>(
    '/api/v1/BidEntryManagement/GetBidReqChangeAuditLogList',
    params,
    undefined,
    headers,
    {
      host: buildVars.VP_BMH_BASE,
    }
  );
}

export type CompareChangeLogList = { newAuditId: number; oldAuditId: number };

export type CompareChangeLogListResponse = {
  compareDetails: {
    newValue: string;
    oldValue: string;
    propertyName: string;
    status: string | null;
  }[];
  new_lastChangedBy: string;
  new_lastChangedDate: string;
  old_lastChangedBy: string;
  old_lastChangedDate: string;
};

export function useGetCompareChangeLogList(params: CompareChangeLogList) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();
  return useApiQuery<CompareChangeLogListResponse>(
    '/api/v1/BidEntryManagement/GetComparedChangeLogList',
    params,
    undefined,
    headers,
    {
      host: buildVars.VP_BMH_BASE,
    }
  );
}

export function useGetBemAuditLogs(
  query: AuditSearchRequest,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<AuditLogsResponse>(
    '/api/v1/BidEntryManagement/SearchBEMAuditLogList',
    query,
    { enabled },
    headers,
    {
      host: buildVars.VP_BMH_BASE,
    }
  );
}

export function useGetBemAuditEventListFilter(
  query: AuditFilterRequest,
  enabled?: boolean
) {
  const buildVars = useBuildVars();
  const headers = useApiHeaders();

  return useApiQuery<AuditFilterResponse>(
    '/api/v1/BidEntryManagement/GetBemAuditEventListFilter',
    query,
    { enabled },
    headers,
    {
      host: buildVars.VP_BMH_BASE,
    }
  );
}
